/**
 * Typed API resources. One function per endpoint, grouped by domain.
 */

import { api, buildQuery, type Page } from "./client";
import type {
  AiProvider,
  AuthSession,
  BudgetStatus,
  Contradiction,
  EmailMessage,
  FactDetail,
  FollowUpQuestion,
  Invitation,
  InvitationPreview,
  InterviewSession,
  KnowledgeFact,
  KnowledgeGap,
  LibraryQuestion,
  Member,
  Membership,
  ModuleExport,
  Lookup,
  OnboardingProject,
  Organization,
  OrganizationDashboard,
  OrganizationSummary,
  PipelineResult,
  PlatformDashboard,
  QuestionAuditEntry,
  QuestionResponse,
  RegistryRecord,
  RegistrySchema,
  Role,
  SessionDetail,
  SignedInUser,
  SurveyModule,
  UploadDetail,
  UploadedSource,
  AssistantAvailability,
  AssistantHealth,
  AssistantOverview,
  AssistantTotals,
  AssistantTraceHealth,
  BreakdownBucket,
  ModelPrice,
  AssistantClassification,
  AssistantDestination,
  AssistantConversation,
  AssistantMessage,
  AssistantStats,
  AssistantTool,
  AssistantToolCall,
  AssistantTurn,
  UsageEvent,
  UsageSummary,
} from "../lib/types";

const org = (organizationId: string) => `/organizations/${organizationId}`;

// --------------------------------------------------------------------- meta

export const lookups = {
  all: () => api.get<Record<string, Lookup[]>>("/lookups"),
  capabilities: () =>
    api.get<{
      supported_upload_extensions: string[];
      row_structured_categories: string[];
      extraction_providers: string[];
    }>("/lookups/capabilities"),
};

export const dashboard = {
  platform: () => api.get<PlatformDashboard>("/dashboard"),
  organization: (organizationId: string) =>
    api.get<OrganizationDashboard>(`${org(organizationId)}/dashboard`),
};

// --------------------------------------------------------------------- organizations

export const organizations = {
  list: (params: { q?: string; limit?: number; offset?: number } = {}) =>
    api.get<Page<OrganizationSummary>>(`/organizations${buildQuery(params)}`),
  get: (organizationId: string) => api.get<Organization>(org(organizationId)),
  create: (payload: Partial<Organization> & { legal_name: string }) =>
    api.post<Organization>("/organizations", payload),
  update: (organizationId: string, payload: Partial<Organization>) =>
    api.patch<Organization>(org(organizationId), payload),
  remove: (organizationId: string) => api.delete<void>(org(organizationId)),

  contacts: (organizationId: string) =>
    api.get<Array<Record<string, unknown>>>(`${org(organizationId)}/contacts`),
  addContact: (organizationId: string, payload: Record<string, unknown>) =>
    api.post<Record<string, unknown>>(
      `${org(organizationId)}/contacts`,
      payload,
    ),

  departments: (organizationId: string) =>
    api.get<Array<Record<string, unknown>>>(
      `${org(organizationId)}/departments`,
    ),
  addDepartment: (organizationId: string, payload: Record<string, unknown>) =>
    api.post<Record<string, unknown>>(
      `${org(organizationId)}/departments`,
      payload,
    ),

  people: (organizationId: string) =>
    api.get<Array<Record<string, unknown>>>(`${org(organizationId)}/people`),
  addPerson: (organizationId: string, payload: Record<string, unknown>) =>
    api.post<Record<string, unknown>>(`${org(organizationId)}/people`, payload),
};

// --------------------------------------------------------------------- onboarding

export const projects = {
  list: (organizationId: string) =>
    api.get<OnboardingProject[]>(`${org(organizationId)}/projects`),
  get: (organizationId: string, projectId: string) =>
    api.get<OnboardingProject>(`${org(organizationId)}/projects/${projectId}`),
  create: (
    organizationId: string,
    payload: { name: string; selected_modules: string[]; notes?: string },
  ) => api.post<OnboardingProject>(`${org(organizationId)}/projects`, payload),
  update: (
    organizationId: string,
    projectId: string,
    payload: Partial<OnboardingProject>,
  ) =>
    api.patch<OnboardingProject>(
      `${org(organizationId)}/projects/${projectId}`,
      payload,
    ),
  runPipeline: (organizationId: string, projectId: string) =>
    api.post<PipelineResult>(
      `${org(organizationId)}/projects/${projectId}/pipeline`,
    ),
  complete: (organizationId: string, projectId: string) =>
    api.post<OnboardingProject>(
      `${org(organizationId)}/projects/${projectId}/complete`,
    ),
  completion: (organizationId: string, projectId: string) =>
    api.get<Record<string, unknown>>(
      `${org(organizationId)}/projects/${projectId}/completion`,
    ),

  gaps: (
    organizationId: string,
    projectId: string,
    params: { status_filter?: string; severity?: string } = {},
  ) =>
    api.get<KnowledgeGap[]>(
      `${org(organizationId)}/projects/${projectId}/gaps${buildQuery(params)}`,
    ),
  waiveGap: (
    organizationId: string,
    projectId: string,
    gapId: string,
    payload: { note: string; actor?: string },
  ) =>
    api.post<KnowledgeGap>(
      `${org(organizationId)}/projects/${projectId}/gaps/${gapId}/waive`,
      payload,
    ),

  followUps: (
    organizationId: string,
    projectId: string,
    statusFilter = "pending",
  ) =>
    api.get<FollowUpQuestion[]>(
      `${org(organizationId)}/projects/${projectId}/follow-ups${buildQuery({
        status_filter: statusFilter,
      })}`,
    ),
  dismissFollowUp: (
    organizationId: string,
    projectId: string,
    followUpId: string,
  ) =>
    api.post<FollowUpQuestion>(
      `${org(organizationId)}/projects/${projectId}/follow-ups/${followUpId}/dismiss`,
    ),
};

// --------------------------------------------------------------------- question library

export const questionLibrary = {
  modules: (params: { applies_to?: string; industry?: string } = {}) =>
    api.get<SurveyModule[]>(`/question-library/modules${buildQuery(params)}`),
  module: (key: string) =>
    api.get<SurveyModule & { questions: LibraryQuestion[] }>(
      `/question-library/modules/${key}`,
    ),
  questions: (params: { module_key?: string; q?: string } = {}) =>
    api.get<LibraryQuestion[]>(
      `/question-library/questions${buildQuery(params)}`,
    ),
  compile: (modules: string[], title?: string) =>
    api.get<{
      survey: Record<string, unknown>;
      questions: unknown[];
      module_keys: string[];
    }>(`/question-library/compile${buildQuery({ modules, title })}`),
  validate: () =>
    api.get<{ valid: boolean; errors: string[]; module_count: number }>(
      "/question-library/validate",
    ),
  sync: () =>
    api.post<{ modules: number; questions: number; templates: number }>(
      "/question-library/sync",
    ),
};

// --------------------------------------------------------------------- interviews

export const sessions = {
  list: (
    organizationId: string,
    params: {
      status_filter?: string;
      project_id?: string;
      limit?: number;
    } = {},
  ) =>
    api.get<Page<InterviewSession>>(
      `${org(organizationId)}/sessions${buildQuery(params)}`,
    ),
  get: (organizationId: string, sessionId: string) =>
    api.get<SessionDetail>(`${org(organizationId)}/sessions/${sessionId}`),
  create: (
    organizationId: string,
    payload: {
      onboarding_project_id?: string;
      module_keys?: string[];
      session_type?: string;
      title?: string;
      person_id?: string;
      department_id?: string;
    },
  ) => api.post<SessionDetail>(`${org(organizationId)}/sessions`, payload),
  saveState: (
    organizationId: string,
    sessionId: string,
    payload: {
      data: Record<string, unknown>;
      current_page_no: number;
      progress_percent?: number;
      actor?: string;
    },
  ) =>
    api.put<SessionDetail>(
      `${org(organizationId)}/sessions/${sessionId}/state`,
      payload,
    ),
  pause: (organizationId: string, sessionId: string) =>
    api.post<InterviewSession>(
      `${org(organizationId)}/sessions/${sessionId}/pause`,
    ),
  resume: (organizationId: string, sessionId: string) =>
    api.post<SessionDetail>(
      `${org(organizationId)}/sessions/${sessionId}/resume`,
    ),
  submit: (
    organizationId: string,
    sessionId: string,
    payload: {
      data: Record<string, unknown>;
      current_page_no: number;
      actor?: string;
    },
  ) =>
    api.post<{ session: InterviewSession; pipeline: PipelineResult | null }>(
      `${org(organizationId)}/sessions/${sessionId}/submit`,
      payload,
    ),
  responses: (organizationId: string, sessionId: string) =>
    api.get<QuestionResponse[]>(
      `${org(organizationId)}/sessions/${sessionId}/responses`,
    ),
};

// --------------------------------------------------------------------- uploads

export const uploads = {
  list: (
    organizationId: string,
    params: {
      category?: string;
      processing_status?: string;
      limit?: number;
    } = {},
  ) =>
    api.get<Page<UploadedSource>>(
      `${org(organizationId)}/uploads${buildQuery(params)}`,
    ),
  get: (organizationId: string, sourceId: string) =>
    api.get<UploadDetail>(`${org(organizationId)}/uploads/${sourceId}`),
  capabilities: (organizationId: string) =>
    api.get<{
      supported_extensions: string[];
      categories: Array<{
        code: string;
        label: string;
        description: string | null;
      }>;
      max_upload_bytes: number;
      row_structured_categories: string[];
    }>(`${org(organizationId)}/uploads/capabilities`),
  create: (organizationId: string, form: FormData) =>
    api.upload<UploadDetail>(`${org(organizationId)}/uploads`, form),
  reparse: (organizationId: string, sourceId: string) =>
    api.post<UploadDetail>(
      `${org(organizationId)}/uploads/${sourceId}/reparse`,
    ),
  rows: (
    organizationId: string,
    sourceId: string,
    params: { limit?: number } = {},
  ) =>
    api.get<
      Array<{ id: string; row_index: number; data: Record<string, unknown> }>
    >(`${org(organizationId)}/uploads/${sourceId}/rows${buildQuery(params)}`),
  downloadUrl: (organizationId: string, sourceId: string) =>
    api.download(`${org(organizationId)}/uploads/${sourceId}/download`),
  remove: (organizationId: string, sourceId: string) =>
    api.delete<void>(`${org(organizationId)}/uploads/${sourceId}`),
};

// --------------------------------------------------------------------- knowledge

export const knowledge = {
  facts: (
    organizationId: string,
    params: {
      verification_status?: string;
      subject_type?: string;
      predicate?: string;
      source_type?: string;
      q?: string;
      limit?: number;
      offset?: number;
    } = {},
  ) =>
    api.get<Page<KnowledgeFact>>(
      `${org(organizationId)}/knowledge/facts${buildQuery(params)}`,
    ),
  fact: (organizationId: string, factId: string) =>
    api.get<FactDetail>(`${org(organizationId)}/knowledge/facts/${factId}`),
  stats: (organizationId: string) =>
    api.get<{
      by_verification_status: Record<string, number>;
      by_subject_type: Record<string, number>;
      by_source_type: Record<string, number>;
    }>(`${org(organizationId)}/knowledge/facts/stats`),
  confirm: (
    organizationId: string,
    factId: string,
    payload: { actor?: string; reason?: string },
  ) =>
    api.post<KnowledgeFact>(
      `${org(organizationId)}/knowledge/facts/${factId}/confirm`,
      payload,
    ),
  reject: (
    organizationId: string,
    factId: string,
    payload: { actor?: string; reason?: string },
  ) =>
    api.post<KnowledgeFact>(
      `${org(organizationId)}/knowledge/facts/${factId}/reject`,
      payload,
    ),
  dispute: (
    organizationId: string,
    factId: string,
    payload: { actor?: string; reason?: string },
  ) =>
    api.post<KnowledgeFact>(
      `${org(organizationId)}/knowledge/facts/${factId}/dispute`,
      payload,
    ),
  edit: (
    organizationId: string,
    factId: string,
    payload: {
      subject_name?: string;
      object_value?: string;
      description?: string;
      actor?: string;
      reason?: string;
      verify?: boolean;
    },
  ) =>
    api.patch<KnowledgeFact>(
      `${org(organizationId)}/knowledge/facts/${factId}`,
      payload,
    ),

  contradictions: (organizationId: string, statusFilter?: string) =>
    api.get<Contradiction[]>(
      `${org(organizationId)}/knowledge/contradictions${buildQuery({ status_filter: statusFilter })}`,
    ),
  resolveContradiction: (
    organizationId: string,
    contradictionId: string,
    payload: {
      resolution: string;
      note?: string;
      new_value?: string;
      actor?: string;
    },
  ) =>
    api.post<Contradiction>(
      `${org(organizationId)}/knowledge/contradictions/${contradictionId}/resolve`,
      payload,
    ),
  dismissContradiction: (
    organizationId: string,
    contradictionId: string,
    payload: { actor?: string; reason?: string },
  ) =>
    api.post<Contradiction>(
      `${org(organizationId)}/knowledge/contradictions/${contradictionId}/dismiss`,
      payload,
    ),
};

// --------------------------------------------------------------------- registries

export const registry = {
  resources: (organizationId: string) =>
    api.get<{ resources: string[] }>(`${org(organizationId)}/registry`),
  schema: (organizationId: string, resource: string) =>
    api.get<RegistrySchema>(
      `${org(organizationId)}/registry/${resource}/schema`,
    ),
  list: (
    organizationId: string,
    resource: string,
    params: Record<string, string | number | undefined> = {},
  ) =>
    api.get<Page<RegistryRecord>>(
      `${org(organizationId)}/registry/${resource}${buildQuery(params)}`,
    ),
  get: (organizationId: string, resource: string, recordId: string) =>
    api.get<RegistryRecord>(
      `${org(organizationId)}/registry/${resource}/${recordId}`,
    ),
  create: (
    organizationId: string,
    resource: string,
    payload: Record<string, unknown>,
  ) =>
    api.post<RegistryRecord>(
      `${org(organizationId)}/registry/${resource}`,
      payload,
    ),
  update: (
    organizationId: string,
    resource: string,
    recordId: string,
    payload: Record<string, unknown>,
  ) =>
    api.patch<RegistryRecord>(
      `${org(organizationId)}/registry/${resource}/${recordId}`,
      payload,
    ),
  remove: (organizationId: string, resource: string, recordId: string) =>
    api.delete<void>(`${org(organizationId)}/registry/${resource}/${recordId}`),
};

// --------------------------------------------------------------------- access

export const auth = {
  config: () =>
    api.get<{ auth_required: boolean; invitation_expiry_days: number }>(
      "/auth/config",
    ),
  login: (email: string, password: string) =>
    api.post<AuthSession>("/auth/login", { email, password }),
  me: () =>
    api.get<{ user: SignedInUser; memberships: Membership[] }>("/auth/me"),
  changePassword: (currentPassword: string | null, newPassword: string) =>
    api.post<SignedInUser>("/auth/password", {
      current_password: currentPassword,
      new_password: newPassword,
    }),
  roles: () => api.get<Role[]>("/auth/roles"),
  /** Public: what an invitation link is for, before anybody sets a password. */
  previewInvitation: (token: string) =>
    api.get<InvitationPreview>(`/auth/invitations/${token}`),
  acceptInvitation: (
    token: string,
    payload: { password?: string; full_name?: string },
  ) => api.post<AuthSession>(`/auth/invitations/${token}/accept`, payload),
};

export const members = {
  list: (organizationId: string) =>
    api.get<Member[]>(`${org(organizationId)}/members`),
  changeRole: (
    organizationId: string,
    membershipId: string,
    roleCode: string,
  ) =>
    api.patch<Member>(`${org(organizationId)}/members/${membershipId}`, {
      role_code: roleCode,
    }),
  remove: (organizationId: string, membershipId: string) =>
    api.delete<void>(`${org(organizationId)}/members/${membershipId}`),
  assignableRoles: (organizationId: string) =>
    api.get<string[]>(`${org(organizationId)}/assignable-roles`),
};

export const invitations = {
  list: (organizationId: string, statusFilter?: string) =>
    api.get<Invitation[]>(
      `${org(organizationId)}/invitations${buildQuery({ status_filter: statusFilter })}`,
    ),
  create: (
    organizationId: string,
    payload: {
      email: string;
      role_code: string;
      full_name?: string;
      message?: string;
      onboarding_project_id?: string;
      interview_session_id?: string;
      person_id?: string;
    },
  ) => api.post<Invitation>(`${org(organizationId)}/invitations`, payload),
  createMany: (
    organizationId: string,
    rows: Array<{
      email: string;
      role_code: string;
      full_name?: string;
      message?: string;
    }>,
  ) =>
    api.post<{
      sent: Invitation[];
      failed: Array<{ email: string; reason: string }>;
    }>(`${org(organizationId)}/invitations/bulk`, { invitations: rows }),
  fromPeople: (
    organizationId: string,
    roleCode = "contributor",
    message?: string,
  ) =>
    api.post<{
      sent: Invitation[];
      failed: Array<{ email: string; reason: string }>;
    }>(
      `${org(organizationId)}/invitations/from-people${buildQuery({ role_code: roleCode, message })}`,
    ),
  resend: (organizationId: string, invitationId: string) =>
    api.post<Invitation>(
      `${org(organizationId)}/invitations/${invitationId}/resend`,
    ),
  revoke: (organizationId: string, invitationId: string) =>
    api.post<Invitation>(
      `${org(organizationId)}/invitations/${invitationId}/revoke`,
    ),
  emails: (organizationId: string, invitationId: string) =>
    api.get<EmailMessage[]>(
      `${org(organizationId)}/invitations/${invitationId}/emails`,
    ),
};

// --------------------------------------------------------------------- AI

export const ai = {
  providers: (organizationId: string) =>
    api.get<AiProvider[]>(`${org(organizationId)}/ai/providers`),
  kinds: (organizationId: string) =>
    api.get<{ kinds: string[] }>(`${org(organizationId)}/ai/provider-kinds`),
  createProvider: (organizationId: string, payload: Record<string, unknown>) =>
    api.post<AiProvider>(`${org(organizationId)}/ai/providers`, payload),
  updateProvider: (
    organizationId: string,
    providerId: string,
    payload: Record<string, unknown>,
  ) =>
    api.patch<AiProvider>(
      `${org(organizationId)}/ai/providers/${providerId}`,
      payload,
    ),
  removeProvider: (organizationId: string, providerId: string) =>
    api.delete<void>(`${org(organizationId)}/ai/providers/${providerId}`),
  summary: (
    organizationId: string,
    params: { period_start?: string; period_end?: string } = {},
  ) =>
    api.get<UsageSummary>(
      `${org(organizationId)}/ai/usage/summary${buildQuery(params)}`,
    ),
  usage: (
    organizationId: string,
    params: {
      limit?: number;
      offset?: number;
      operation?: string;
      status?: string;
    } = {},
  ) =>
    api.get<Page<UsageEvent>>(
      `${org(organizationId)}/ai/usage${buildQuery(params)}`,
    ),
  budgets: (organizationId: string) =>
    api.get<BudgetStatus[]>(`${org(organizationId)}/ai/budgets`),
  overview: (organizationId: string, days = 30) =>
    api.get<AssistantOverview>(`${org(organizationId)}/ai/overview${buildQuery({ days })}`),
  health: (organizationId: string) =>
    api.get<AssistantHealth>(`${org(organizationId)}/ai/health`),
  traceHealth: (organizationId: string, days = 30) =>
    api.get<AssistantTraceHealth>(
      `${org(organizationId)}/ai/trace-health${buildQuery({ days })}`,
    ),
  prices: (organizationId: string) =>
    api.get<ModelPrice[]>(`${org(organizationId)}/ai/prices`),
  breakdowns: (organizationId: string) =>
    api.get<Record<string, BreakdownBucket[]>>(`${org(organizationId)}/ai/usage/breakdowns`),
  reconciliation: (organizationId: string) =>
    api.get<{
      agrees: boolean;
      disagreements: string[];
      organization: AssistantTotals;
      by_user: string;
      by_model: string;
    }>(`${org(organizationId)}/ai/usage/reconciliation`),
  estimate: (
    organizationId: string,
    promptTokens: number,
    completionTokens: number,
  ) =>
    api.get<{
      configured: boolean;
      provider_name?: string;
      model?: string;
      cost_amount: string | null;
      currency: string | null;
      budget?: BudgetStatus;
    }>(
      `${org(organizationId)}/ai/estimate${buildQuery({
        prompt_tokens: promptTokens,
        completion_tokens: completionTokens,
      })}`,
    ),
};

// --------------------------------------------------------------------- assistant

export const assistant = {
  availability: (organizationId: string) =>
    api.get<AssistantAvailability>(`${org(organizationId)}/assistant/availability`),
  tools: (organizationId: string) =>
    api.get<AssistantTool[]>(`${org(organizationId)}/assistant/tools`),
  retention: (organizationId: string) =>
    api.get<{ content_retention_days: number; ledger_retention: string; note: string }>(
      `${org(organizationId)}/assistant/retention`,
    ),
  conversations: (
    organizationId: string,
    params: {
      scope?: "mine" | "everyone";
      q?: string;
      archived?: boolean;
      mode?: string;
      linked_interview_id?: string;
      limit?: number;
      offset?: number;
    } = {},
  ) =>
    api.get<Page<AssistantConversation>>(
      `${org(organizationId)}/assistant/conversations${buildQuery(params)}`,
    ),
  createConversation: (organizationId: string, payload: Record<string, unknown>) =>
    api.post<AssistantConversation>(
      `${org(organizationId)}/assistant/conversations`,
      payload,
    ),
  conversation: (organizationId: string, conversationId: string) =>
    api.get<AssistantConversation>(
      `${org(organizationId)}/assistant/conversations/${conversationId}`,
    ),
  updateConversation: (
    organizationId: string,
    conversationId: string,
    payload: Record<string, unknown>,
  ) =>
    api.patch<AssistantConversation>(
      `${org(organizationId)}/assistant/conversations/${conversationId}`,
      payload,
    ),
  deleteConversation: (organizationId: string, conversationId: string) =>
    api.delete<void>(`${org(organizationId)}/assistant/conversations/${conversationId}`),
  messages: (organizationId: string, conversationId: string) =>
    api.get<AssistantMessage[]>(
      `${org(organizationId)}/assistant/conversations/${conversationId}/messages`,
    ),
  toolCalls: (organizationId: string, conversationId: string) =>
    api.get<AssistantToolCall[]>(
      `${org(organizationId)}/assistant/conversations/${conversationId}/tool-calls`,
    ),
  stats: (organizationId: string, conversationId: string) =>
    api.get<AssistantStats>(
      `${org(organizationId)}/assistant/conversations/${conversationId}/stats`,
    ),
  /** Blocking: one request, one answer, one trace. Streaming is deliberately not in v1. */
  turn: (
    organizationId: string,
    conversationId: string,
    payload: { text: string; operation?: string; context?: Record<string, unknown> },
  ) =>
    api.post<AssistantTurn>(
      `${org(organizationId)}/assistant/conversations/${conversationId}/turns`,
      payload,
    ),
  /** The only path that executes a write the assistant proposed. */
  confirmToolCall: (
    organizationId: string,
    conversationId: string,
    toolCallId: string,
    args?: Record<string, unknown>,
  ) =>
    api.post<AssistantToolCall>(
      `${org(organizationId)}/assistant/conversations/${conversationId}/tool-calls/${toolCallId}/confirm`,
      { arguments: args ?? null },
    ),
  declineToolCall: (
    organizationId: string,
    conversationId: string,
    toolCallId: string,
  ) =>
    api.post<AssistantToolCall>(
      `${org(organizationId)}/assistant/conversations/${conversationId}/tool-calls/${toolCallId}/decline`,
      {},
    ),
  feedback: (
    organizationId: string,
    messageId: string,
    payload: { rating: number; reason?: string | null; comment?: string | null },
  ) =>
    api.post<{ id: string; rating: number }>(
      `${org(organizationId)}/assistant/messages/${messageId}/feedback`,
      payload,
    ),
};

export const classification = {
  destinations: (organizationId: string) =>
    api.get<{ destinations: AssistantDestination[]; precedence: string[] }>(
      `${org(organizationId)}/assistant/classifications/destinations`,
    ),
  list: (
    organizationId: string,
    params: { status?: string; needs_review?: boolean; limit?: number } = {},
  ) =>
    api.get<AssistantClassification[]>(
      `${org(organizationId)}/assistant/classifications${buildQuery(params)}`,
    ),
  classify: (organizationId: string, responseId: string, useModel = true) =>
    api.post<AssistantClassification>(`${org(organizationId)}/assistant/classifications`, {
      response_id: responseId,
      use_model: useModel,
    }),
  /** Accepts the ROUTING. Confirms nothing about the answer. */
  accept: (organizationId: string, id: string, note?: string) =>
    api.post<AssistantClassification>(
      `${org(organizationId)}/assistant/classifications/${id}/accept`,
      { note: note ?? null },
    ),
  change: (organizationId: string, id: string, payload: Record<string, unknown>) =>
    api.post<AssistantClassification>(
      `${org(organizationId)}/assistant/classifications/${id}/change`,
      payload,
    ),
  clarify: (organizationId: string, id: string, question?: string) =>
    api.post<AssistantClassification>(
      `${org(organizationId)}/assistant/classifications/${id}/clarify`,
      { question: question ?? null },
    ),
};

// --------------------------------------------------------------------- question authoring

export const authoring = {
  updateModule: (moduleKey: string, payload: Record<string, unknown>) =>
    api.patch<SurveyModule>(`/question-library/modules/${moduleKey}`, payload),
  addQuestion: (moduleKey: string, payload: Record<string, unknown>) =>
    api.post<LibraryQuestion>(
      `/question-library/modules/${moduleKey}/questions`,
      payload,
    ),
  updateQuestion: (
    moduleKey: string,
    semanticId: string,
    payload: Record<string, unknown>,
  ) =>
    api.patch<LibraryQuestion>(
      `/question-library/modules/${moduleKey}/questions/${semanticId}`,
      payload,
    ),
  retireQuestion: (moduleKey: string, semanticId: string, reason?: string) =>
    api.post<LibraryQuestion>(
      `/question-library/modules/${moduleKey}/questions/${semanticId}/retire`,
      { reason },
    ),
  restoreQuestion: (moduleKey: string, semanticId: string) =>
    api.post<LibraryQuestion>(
      `/question-library/modules/${moduleKey}/questions/${semanticId}/restore`,
    ),
  deleteQuestion: (moduleKey: string, semanticId: string) =>
    api.delete<void>(
      `/question-library/modules/${moduleKey}/questions/${semanticId}`,
    ),
  reorder: (moduleKey: string, semanticIds: string[]) =>
    api.post<LibraryQuestion[]>(
      `/question-library/modules/${moduleKey}/reorder`,
      {
        semantic_ids: semanticIds,
      },
    ),
  exportModule: (moduleKey: string) =>
    api.get<ModuleExport>(`/question-library/modules/${moduleKey}/export`),
  audit: (
    params: { module_key?: string; semantic_id?: string; limit?: number } = {},
  ) =>
    api.get<QuestionAuditEntry[]>(
      `/question-library/audit${buildQuery(params)}`,
    ),
};

// --------------------------------------------------------------------- enterprise context (read side)

export interface EcBinding {
  tenant_slug: string | null;
  status: "UNCONFIGURED" | "UNVERIFIED" | "VERIFIED" | "INCOMPATIBLE" | "ERROR";
  last_verified_at: string | null;
  error_class: string | null;
  error: string | null;
}

export interface EcPack {
  key: string;
  version: string;
  description: string | null;
  depth: number;
  requires: string[];
  concept_count: number;
  position: number;
}

export interface EcGap {
  gap_key: string;
  gap: string;
  concept: string;
  member: string | null;
  severity: string;
  message: string;
  open_candidates: number;
  first_seen_at: string;
  last_seen_at: string;
  resolved_at: string | null;
}

/** A local projection of what Enterprise Context answered. `authoritative` is always false. */
export interface EcContext {
  source: "enterprise-context";
  authoritative: false;
  binding: EcBinding;
  contract: {
    discovery_export_expected: string;
    discovery_export_server: string | null;
    api_server: string | null;
    compatible: boolean | null;
  };
  packs: EcPack[];
  gaps: EcGap[];
  gap_summary: Record<string, number>;
  /** PUBLISHED EC definitions for concepts our questions target — what suppression reads. */
  definitions: EcDefinition[];
  /** context_targets whose concept the tenant's vocabulary does not contain (never guessed). */
  target_warnings: { semantic_id: string; concept: string }[];
  freshness: {
    refresh_status: "NEVER" | "OK" | "PARTIAL" | "FAILED";
    last_refresh_at: string | null;
    last_successful_refresh_at: string | null;
    stale: boolean;
  };
}

export interface EcDefinition {
  concept: string;
  kind: string;
  subject_key: string;
  field: string | null;
  definition_id: string;
  version: number;
  status: string;
  effective_from: string | null;
  effective_to: string | null;
  observed_at: string;
}

export interface EcDiscovery {
  source: "enterprise-context" | "none";
  module_keys: string[];
  packs: { ec_pack: string; discovery_pack: string; modules: string[] }[];
  reason: string;
}

export interface EcRefresh {
  binding: string;
  refresh: string;
  contract: string | null;
  packs: number | null;
  gaps: number | null;
  refreshed_at: string;
  error_class: string | null;
  error: string | null;
}

/** One exported observation. `status` is TRANSPORT (EC has the candidate); the governance fields
 *  are READ BACK from Enterprise Context; `governance_state` is derived from both. */
export interface EcExport {
  fact_id: string;
  status:
    | "NOT_EXPORTABLE"
    | "PENDING"
    | "SYNCED"
    | "FAILED_RETRYABLE"
    | "FAILED_PERMANENT";
  candidate_id: string | null;
  last_error: string | null;
  synced_at: string | null;
  concept_source: string | null;
  destination: string | null;
  candidate_status: string | null;
  resulting_definition_id: string | null;
  definition_status: string | null;
  published_at: string | null;
  decision_reason: string | null;
  governance_checked_at: string | null;
  governance_error: string | null;
  governance_state:
    | "Not submitted"
    | "Submitted"
    | "Under review"
    | "Approved"
    | "Published"
    | "Rejected"
    | "Superseded";
}

export interface EcGovernanceRefresh {
  checked: number;
  changed: number;
  failed: number;
  published: number;
  rejected: number;
  checked_at: string | null;
  errors: string[];
}

export interface ConceptPreview {
  concept: string;
  preferred_label: string;
  pack: string | null;
  questions: string[];
  published: {
    definition_id: string;
    kind: string;
    field: string | null;
    value: string | null;
    subject_key: string;
    version: number;
    observed_at: string;
  }[];
  heard: {
    fact_id: string;
    kind: string | null;
    field: string | null;
    value: string;
    status: string;
    human_confirmed: boolean;
    subject: string;
    predicate: string;
    semantic_id: string | null;
    governance_state: string;
    candidate_id: string | null;
  }[];
  confirmed_count: number;
  proposed_count: number;
  differences: {
    kind: string;
    field: string | null;
    published: string;
    confirmed: string;
    fact_id: string;
    definition_id: string;
  }[];
  open_task_count: number;
}

export interface EcSettings {
  organization_id: string;
  tenant_slug: string;
  concept_map: Record<string, string>;
  export_statuses: string[];
  enabled: boolean;
}

export const enterpriseContext = {
  context: (organizationId: string) =>
    api.get<EcContext>(`${org(organizationId)}/enterprise-context/context`),
  settings: (organizationId: string) =>
    api.get<EcSettings | null>(
      `${org(organizationId)}/enterprise-context/settings`,
    ),
  link: (organizationId: string, tenantSlug: string) =>
    api.put<EcSettings>(`${org(organizationId)}/enterprise-context/settings`, {
      tenant_slug: tenantSlug,
    }),
  verify: (organizationId: string) =>
    api.post<EcBinding>(`${org(organizationId)}/enterprise-context/verify`, {}),
  refresh: (organizationId: string) =>
    api.post<EcRefresh>(
      `${org(organizationId)}/enterprise-context/refresh`,
      {},
    ),
  discovery: (organizationId: string) =>
    api.get<EcDiscovery>(`${org(organizationId)}/enterprise-context/discovery`),
  exports: (organizationId: string) =>
    api.get<EcExport[]>(`${org(organizationId)}/enterprise-context/exports`),
  preview: (organizationId: string) =>
    api.get<ConceptPreview[]>(
      `${org(organizationId)}/enterprise-context/preview`,
    ),
  ask: (organizationId: string, concept: string, reason?: string) =>
    api.post<{ task_id: string; semantic_ids: string[]; status: string }>(
      `${org(organizationId)}/enterprise-context/preview/ask`,
      { concept, reason },
    ),
  refreshGovernance: (organizationId: string) =>
    api.post<EcGovernanceRefresh>(
      `${org(organizationId)}/enterprise-context/exports/refresh`,
      {},
    ),
};

// --------------------------------------------------------------------- context tasks (S5)

export type TaskType =
  | "ANSWER_QUESTION"
  | "CONFIRM_OBSERVATION"
  | "CLARIFY_CONFLICT"
  | "UPLOAD_EVIDENCE"
  | "REVIEW_CHANGE"
  | "RECERTIFY_CONTEXT";

export interface ContextTask {
  id: string;
  organization_id: string;
  onboarding_project_id: string | null;
  task_type: TaskType;
  status: "open" | "in_progress" | "done" | "waived" | "superseded";
  priority: "high" | "normal" | "low";
  title: string;
  reason: string | null;
  origin: string;
  origin_reference: string;
  semantic_ids: string[];
  concept: string | null;
  target_kind: string | null;
  assigned_person_id: string | null;
  assigned_department_id: string | null;
  assigned_user_id: string | null;
  role_hint: string | null;
  due_at: string | null;
  session_id: string | null;
  result_fact_id: string | null;
  result_response_id: string | null;
  contradiction_id: string | null;
  resolved_at: string | null;
  resolved_by: string | null;
  resolution_note: string | null;
  created_at: string;
  updated_at: string;
}

export interface TaskSummary {
  open: number;
  by_type: Record<string, number>;
  questions: number;
  confirmations: number;
  conflicts: number;
  uploads: number;
  unassigned: number;
  scope: "mine" | "everyone";
}

export const tasks = {
  list: (
    organizationId: string,
    params: {
      scope?: "mine" | "everyone";
      status?: string;
      task_type?: string;
    } = {},
  ) =>
    api.get<ContextTask[]>(`${org(organizationId)}/tasks${buildQuery(params)}`),
  summary: (organizationId: string, scope: "mine" | "everyone" = "mine") =>
    api.get<TaskSummary>(
      `${org(organizationId)}/tasks/summary${buildQuery({ scope })}`,
    ),
  derive: (organizationId: string) =>
    api.post<{
      created: number;
      reopened: number;
      closed: number;
      routed: number;
      unchanged: number;
      skipped: string[];
    }>(`${org(organizationId)}/tasks/derive`, {}),
  start: (organizationId: string, taskId: string) =>
    api.post<{ id: string }>(
      `${org(organizationId)}/tasks/${taskId}/start`,
      {},
    ),
  assign: (
    organizationId: string,
    taskId: string,
    payload: { person_id?: string; department_id?: string },
  ) =>
    api.post<ContextTask>(
      `${org(organizationId)}/tasks/${taskId}/assign`,
      payload,
    ),
  waive: (organizationId: string, taskId: string, note: string) =>
    api.post<ContextTask>(`${org(organizationId)}/tasks/${taskId}/waive`, {
      note,
    }),
};
